import { useMemo } from "react";
import { useLocation } from "react-router";
import { useGetSelectableFieldOption } from "@certa/queries/queries/reports/getSelectableFieldOption.query";
import { useGetFieldLabelValueOptions } from "@certa/queries/queries/reports/getFieldLabelValueOptions.query";
import queryString from "query-string";
import type { SelectOption } from "@certa/catalyst/components/Select";
import type { FieldTypes } from "@certa/types/src/fieldTypes";
import { useGetBoolOptions } from "../../../../../hooks/useGetBoolOptions";
import { useReportBadges } from "@certa/dashboards/src/components/createReport/ObjectReports/ReportView/hooks/useReportBadges.ts";
import { createProcessValueTag } from "@certa/common/utils/report";

type UseHandleAdvancedFilterSelectProps = {
  shouldUseNewLabelValueOptions: boolean;
  kindId: string | undefined;
  fieldTag: string;
  fieldType: FieldTypes;
  onChange?: ((value: string | string[] | undefined) => void) | undefined;
  contentType?: "workflow" | "object";
  kindTag?: string;
};

export const useHandleAdvancedFilterSelect = ({
  shouldUseNewLabelValueOptions,
  kindId,
  fieldTag,
  fieldType,
  onChange,
  contentType,
  kindTag
}: UseHandleAdvancedFilterSelectProps) => {
  const location = useLocation();

  // "/search" sets "kind_id" in queryParams already, no need for prop drilling from there
  const { kind_id: queryParamKindId } = queryString.parse(location.search);
  const isBoolField = fieldType === "bool";
  const isBadgeTagsField =
    fieldTag === createProcessValueTag("badge_tags") ||
    fieldTag === "badge_tags";
  const { boolOptions } = useGetBoolOptions();

  // Use badges hook for badge_tags field
  const { badges: allBadges = [], shouldShowBadgeLoadingState } =
    useReportBadges({
      isBadgeTagsEnabled: isBadgeTagsField,
      contentType,
      typeTag: kindTag
    });

  const badgesOptions = useMemo(() => {
    return allBadges.map(badge => ({ label: badge.name, value: badge.tag }));
  }, [allBadges]);

  const { data: getSelectOptionsData = [], isLoading: isLoadingSelectOptions } =
    useGetSelectableFieldOption({
      fieldTag,
      kindId: typeof queryParamKindId === "string" ? queryParamKindId : kindId,
      config: {
        enabled:
          !isBoolField &&
          !isBadgeTagsField &&
          !shouldUseNewLabelValueOptions &&
          !!fieldTag &&
          !!(queryParamKindId || kindId)
      }
    });

  const {
    data: { options: getLabelValueOptionsData = [] } = {},
    isLoading: isLoadingLabelValueOptions
  } = useGetFieldLabelValueOptions({
    kindId,
    fieldTag: fieldTag,
    config: {
      enabled:
        !isBoolField &&
        !isBadgeTagsField &&
        shouldUseNewLabelValueOptions &&
        !!fieldTag &&
        !!kindId
    }
  });

  const selectOptions: SelectOption[] =
    fieldType === "bool"
      ? boolOptions
      : isBadgeTagsField
        ? badgesOptions
        : shouldUseNewLabelValueOptions
          ? getLabelValueOptionsData
          : getSelectOptionsData;

  const isLoading =
    isLoadingSelectOptions ||
    isLoadingLabelValueOptions ||
    shouldShowBadgeLoadingState;

  const handleCreateOptions = (inputValue: string) => {
    onChange?.(inputValue);
  };

  return {
    isLoading,
    selectOptions,
    handleCreateOptions
  };
};
